import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import type { DataTableFormat } from '../__generated__/apollo-hooks';
import type { CodeSearchLanguage } from '../components/code-search/code-search-examples.data';
import type { DataTablesViewMode } from '../components/data-tables-view/data-tables-view.types';
import { LOCAL_STORAGE_USER_PREFERENCES } from '../constants/general';
import type { CategorySortingAlgorithm } from '../helpers/category.helper';
import {
  getObjectFromLocalStorage,
  safeLocalStorage
} from '../helpers/local-storage.helper';
import { createPersistedStore } from './store-creators';

// The resolved app-wide light/dark mode. Drives the design-system CSS-variable theme
// (`data-theme` on <html>) and MUI's palette mode.
export type ColorScheme = 'light' | 'dark';

// The user's theme choice. `system` follows the OS `prefers-color-scheme`;
// `_app` resolves it to a concrete ColorScheme.
export type ThemePreference = ColorScheme | 'system';
const THEME_PREFERENCE_DEFAULT: ThemePreference = 'system';

type ChronoViewMode = 'point-in-time' | 'historical';
const CHRONO_VIEW_MODE_DEFAULT: ChronoViewMode = 'point-in-time';

type UserPreferenceState = {
  builderLayout: '2D' | '3D';
  hasTopologyAsMainContent: boolean;
  show3DGraph: boolean;
  enableMotion: boolean;
  disableKeyboardShortcuts: boolean;
  recipeResultColumnVisibility: GridColumnVisibilityModel;
  dismissedConnectorUpdates: string[];
  previousSearchQueries: string[];
  codeSearchRecentQueries: string[];
  // Last picked, or named by the last search.
  codeSearchLanguage: CodeSearchLanguage | null;
  categorySortingAlgorithm: CategorySortingAlgorithm;
  themePreference: ThemePreference;
  chronoDefaultViewMode: ChronoViewMode;
  chronoHideNA: boolean;
  chronoHideNoLst: boolean;
  navRailCollapsed: boolean;
  // null until the user explicitly picks a view — the page then falls back to
  // a count-based default (see defaultDataTablesView).
  dataTablesView: DataTablesViewMode | null;
  dataTablesDownloadFormat: DataTableFormat;
};

const initialization = (obj) => {
  const old = getObjectFromLocalStorage<UserPreferenceState>(
    LOCAL_STORAGE_USER_PREFERENCES
  );

  if (!old) {
    return obj;
  }
  const {
    disableKeyboardShortcuts,
    recipeResultColumnVisibility,
    dismissedConnectorUpdates,
    previousSearchQueries
  } = old;
  delete old.disableKeyboardShortcuts;
  delete old.recipeResultColumnVisibility;
  delete old.dismissedConnectorUpdates;
  delete old.previousSearchQueries;

  safeLocalStorage.setItem(LOCAL_STORAGE_USER_PREFERENCES, JSON.stringify(old));
  return {
    ...obj,
    disableKeyboardShortcuts,
    recipeResultColumnVisibility,
    dismissedConnectorUpdates,
    previousSearchQueries
  };
};

export const useUserPreferenceStore = createPersistedStore<UserPreferenceState>(
  () => ({
    categorySortingAlgorithm: 'Total recipe count',
    builderLayout: '3D',
    hasTopologyAsMainContent: true,
    show3DGraph: true,
    enableMotion: false,
    disableKeyboardShortcuts: false,
    previousSearchQueries: [],
    codeSearchRecentQueries: [],
    codeSearchLanguage: null,
    recipeResultColumnVisibility: {},
    dismissedConnectorUpdates: [],
    themePreference: THEME_PREFERENCE_DEFAULT,
    chronoDefaultViewMode: CHRONO_VIEW_MODE_DEFAULT,
    chronoHideNA: false,
    chronoHideNoLst: false,
    navRailCollapsed: false,
    dataTablesView: null,
    dataTablesDownloadFormat: 'CSV'
  }),
  {
    name: 'user-preference'
  },
  initialization
);
